import React, { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import styled from 'styled-components'
import { FormattedMessage } from 'gatsby-plugin-intl'
import { breakpoint } from 'src/theme/grid'
import OfferDetail from 'src/features/marketplace/components/OfferDetail'
import CircularProgress from '@material-ui/core/CircularProgress'
import MarketplaceService from 'src/services/MarketplaceService'
import OfferCard from 'src/features/marketplace/components/OfferCard'
import { navigate, useLocation } from '@reach/router'

const Loader = styled(CircularProgress)`
  width: 25px !important;
  height: 25px !important;
  color: ${({ theme }) => theme.colors.apple.hex()} !important;
`

const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  padding: 2rem;
  border-radius: 4px;
  min-width: 200px;
  background: transparent;
`

const OFFER_QUERY_PARAM = 'offerId'

const OffersList = () => {
  const location = useLocation()
  const [selectedOfferId, setSelectedOfferId] = useState(() => {
    const params = new URLSearchParams(location.search)
    return params.get(OFFER_QUERY_PARAM)
  })

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const offerIdFromUrl = params.get(OFFER_QUERY_PARAM)

    setSelectedOfferId(currentId => {
      if (offerIdFromUrl && offerIdFromUrl !== currentId) {
        return offerIdFromUrl
      }

      if (!offerIdFromUrl && currentId) {
        return null
      }

      return currentId
    })
  }, [location.search])

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const currentOfferId = params.get(OFFER_QUERY_PARAM)
    const nextOfferId = selectedOfferId || null

    if (nextOfferId) {
      if (currentOfferId !== nextOfferId) {
        params.set(OFFER_QUERY_PARAM, nextOfferId)
        const searchString = params.toString()
        const targetUrl = `${
          location.pathname
        }?${searchString}${location.hash || ''}`
        navigate(targetUrl, { replace: true })
      }
    } else if (currentOfferId) {
      params.delete(OFFER_QUERY_PARAM)
      const searchString = params.toString()
      const targetUrl = `${location.pathname}${
        searchString ? `?${searchString}` : ''
      }${location.hash || ''}`
      navigate(targetUrl, { replace: true })
    }
  }, [selectedOfferId, location.pathname, location.search, location.hash])

  const offersQuery = useQuery({
    queryKey: ['marketplace-offers'],
    queryFn: MarketplaceService.showOffers,
    refetchOnWindowFocus: false
  })

  const offerQuery = useQuery({
    queryKey: ['marketplace-offers', selectedOfferId],
    queryFn: () =>
      selectedOfferId ? MarketplaceService.showOffer(selectedOfferId) : null,
    enabled: !!selectedOfferId,
    refetchOnWindowFocus: false
  })

  const handleOfferClick = offer => {
    const nextId = String(offer.id)

    if (nextId === selectedOfferId) {
      return
    }

    setSelectedOfferId(nextId)
  }

  const handleCloseDetail = () => {
    setSelectedOfferId(null)
  }

  if (offersQuery.isLoading) {
    return (
      <LoaderContainer>
        <Loader />
      </LoaderContainer>
    )
  }

  if (offersQuery.isError) {
    return (
      <EmptyOffersContainer>
        <div>Error: {offersQuery.error?.message}</div>
      </EmptyOffersContainer>
    )
  }

  // Check whether any offers are available.
  if (!offersQuery.data?.data || offersQuery.data.data.length === 0) {
    return (
      <EmptyOffersContainer>
        <FormattedMessage id='offers.noOffers' />
      </EmptyOffersContainer>
    )
  }

  const sortedOffers = [...offersQuery.data.data].sort((a, b) => {
    if (a.isVip && !b.isVip) return -1
    if (!a.isVip && b.isVip) return 1

    if (a.isVip === b.isVip) {
      const dateA = new Date(a.pubStartDate)
      const dateB = new Date(b.pubStartDate)
      return dateB - dateA
    }

    return 0
  })

  return (
    <>
      <OffersGrid>
        {sortedOffers.map(offer => (
          <OfferCard key={offer.id} offer={offer} onClick={handleOfferClick} />
        ))}
      </OffersGrid>

      {selectedOfferId && (
        <DetailOverlay onClick={handleCloseDetail}>
          {offerQuery.isLoading ? (
            <LoaderContainer onClick={e => e.stopPropagation()}>
              <Loader />
            </LoaderContainer>
          ) : (
            <OfferDetail
              offer={offerQuery.data}
              onClose={handleCloseDetail}
              isLoading={offerQuery.isLoading}
            />
          )}
        </DetailOverlay>
      )}
    </>
  )
}

const EmptyOffersContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.black.hex()};
  text-align: center;
  padding: 2rem;
`

const OffersGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 1.5rem;

  ${breakpoint.s`
    grid-template-columns: repeat(2, minmax(0, 1fr));
  `}
`

const DetailOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`

export default OffersList
